import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from '../../../lib/useAuthToken';
import {
  listSuppliers, createSupplier, updateSupplier, deleteSupplier,
  listOrders, getOrder, createOrder, applyStock,
  getPriceHistory, getPriceOverview,
  type Supplier, type PurchaseOrder,
} from '../api/purchases.client';

const defaults = { staleTime: 60_000, retry: 1 } as const;
const enabled = (token: string | null, extra?: boolean) => !!token && extra !== false;

// ── Suppliers ──────────────────────────────────────────────────────────────────
export const useSuppliers = () => {
  const t = useAuthToken();
  return useQuery({ queryKey: ['purchases', 'suppliers'], queryFn: listSuppliers, enabled: enabled(t), ...defaults });
};

export const useCreateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Supplier>) => createSupplier(data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] }); },
  });
};

export const useUpdateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Supplier> }) => updateSupplier(id, data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] }); },
  });
};

export const useDeleteSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteSupplier(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['purchases', 'suppliers'] }); },
  });
};

// ── Purchase Orders ────────────────────────────────────────────────────────────
export const usePurchaseOrders = (params?: Record<string, unknown>) => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['purchases', 'orders', params],
    queryFn: () => listOrders(params),
    enabled: enabled(t),
    ...defaults,
  });
};

export const usePurchaseOrder = (id?: number) => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['purchases', 'order', id],
    queryFn: () => getOrder(id!),
    enabled: enabled(t, !!id),
    ...defaults,
  });
};

export const useCreateOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createOrder,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchases', 'orders'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
    },
  });
};

export const useApplyStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => applyStock(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchases', 'orders'] });
      void qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
};

// ── Price History ──────────────────────────────────────────────────────────────
export const usePriceHistory = (inventoryItemId?: number, params?: Record<string, unknown>) => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['purchases', 'priceHistory', inventoryItemId, params],
    queryFn: () => getPriceHistory(inventoryItemId!, params),
    enabled: enabled(t, !!inventoryItemId),
    ...defaults,
  });
};

export const usePriceOverview = () => {
  const t = useAuthToken();
  return useQuery({
    queryKey: ['purchases', 'priceOverview'],
    queryFn: async () => {
      const data = await getPriceOverview();
      return Array.isArray(data) ? data : [];
    },
    enabled: enabled(t),
    ...defaults,
  });
};
